/**
 * Lightweight markdown-to-HTML renderer.
 * #7 fix: Replaces the naive regex that broke on lists, links, code blocks.
 * #8 fix: Add table and D2 diagram support so narrative fallback renders correctly.
 * Handles: headings, paragraphs, bold, italic, links, inline code,
 * code blocks, D2 diagrams, pipe tables, lists (ul/ol), blockquotes, horizontal rules.
 */

export function renderMarkdown(md: string, opts?: { brand?: string; slug?: string }): string {
  const _brand = opts?.brand;
  const _slug = opts?.slug;
  let _d2Index = 0;
  if (!md) return '';

  // Strip YAML frontmatter (--- ... ---) at the start of the file
  md = md.replace(/^---[\s\S]*?---\n?/, '');

  const lines = md.split('\n');
  const output: string[] = [];
  let inCodeBlock = false;
  let codeBlockContent: string[] = [];
  let codeBlockLang = '';
  let inList: 'ul' | 'ol' | null = null;
  let inBlockquote = false;
  let paragraph: string[] = [];
  let inTable = false;
  let tableHeaders: string[] = [];
  let tableAligns: ('left' | 'center' | 'right')[] = [];
  let tableRows: string[][] = [];

  function flushParagraph() {
    if (paragraph.length > 0) {
      output.push(`<p>${inlineMarkdown(paragraph.join(' '))}</p>`);
      paragraph = [];
    }
  }

  function flushList() {
    if (inList) {
      output.push(`</${inList}>`);
      inList = null;
    }
  }

  function flushBlockquote() {
    if (inBlockquote) {
      output.push('</blockquote>');
      inBlockquote = false;
    }
  }

  function parseTableRow(line: string): string[] {
    return line.replace(/^\|/, '').replace(/\|$/, '').split('|');
  }

  function isTableSeparator(line: string): boolean {
    return /^\|(\s*:?-{3,}:?\s*\|)+\s*$/.test(line.trim());
  }

  function parseAlignments(line: string): ('left' | 'center' | 'right')[] {
    return parseTableRow(line).map(cell => {
      const t = cell.trim();
      if (t.startsWith(':') && t.endsWith(':')) return 'center';
      if (t.endsWith(':')) return 'right';
      return 'left';
    });
  }

  function flushTable() {
    if (!inTable) return;
    const ths = tableHeaders.map((h, i) =>
      `<th style="text-align:${tableAligns[i] || 'left'}">${inlineMarkdown(h.trim())}</th>`
    ).join('');
    const rows = tableRows.map(row =>
      `<tr>${row.map((cell, i) => `<td style="text-align:${tableAligns[i] || 'left'}">${inlineMarkdown(cell.trim())}</td>`).join('')}</tr>`
    ).join('\n');
    output.push(`<table>\n<thead><tr>${ths}</tr></thead>\n<tbody>\n${rows}\n</tbody>\n</table>`);
    inTable = false;
    tableHeaders = [];
    tableAligns = [];
    tableRows = [];
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Code blocks (including D2 diagrams)
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        if (codeBlockLang === 'd2') {
          // D2 diagrams: when brand+slug context is available, fetch rendered SVG
          // from the server via /api/:brand/pieces/:slug/diagram?n=N. Falls back
          // to a tasteful design-token placeholder when no context (raw preview)
          // OR when the server fails (Satori/Sharp not configured, cache
          // unwritable, malformed D2 source). The fallback prints the D2 source
          // verbatim in a mono box so users still see the diagram intent.
          if (_brand && _slug) {
            const fallbackSource = escapeHtml(codeBlockContent.join('\n'));
            const imgUrl = `/api/${encodeURIComponent(_brand)}/pieces/${encodeURIComponent(_slug)}/diagram?n=${_d2Index}`;
            output.push(
              // Width tier: cap at ~1080px within the preview pane (wider than the
              // 720px text column so wide diagrams get room) and center; no inner
              // scroll box. Mirrors blog.html's tier, bounded by the pane instead
              // of the viewport. The img is wrapped in an anchor for click-to-zoom,
              // so onload/onerror target closest('.d2-diagram'), not parentNode.
              `<div class="d2-diagram" style="max-width:1080px;margin:24px auto;text-align:center;min-height:120px;background:var(--g-50);border:1px solid var(--line)">` +
                `<a href="${imgUrl}" target="_blank" rel="noopener" title="Open full size" style="display:block;cursor:zoom-in">` +
                  `<img src="${imgUrl}" alt="Architecture diagram" loading="lazy" style="max-width:100%;height:auto;display:block;margin:0 auto" ` +
                  `onload="var d=this.closest('.d2-diagram');d.style.background='var(--paper)';d.style.minHeight='auto'" ` +
                  `onerror="var d=this.closest('.d2-diagram');this.style.display='none';d.style.background='var(--paper)';d.style.borderLeft='4px solid var(--zop-orange)';d.style.minHeight='auto';d.style.padding='16px';d.style.color='var(--ink)';d.style.fontFamily='var(--mono)';d.style.fontSize='12px';d.style.whiteSpace='pre-wrap';d.style.textAlign='left';d.innerHTML='<div style=&quot;font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--g-600);margin-bottom:8px&quot;>D2 source · render unavailable</div>${fallbackSource.replace(/'/g, "&#39;")}'"/>` +
                `</a>` +
              `</div>`
            );
            _d2Index++;
          } else {
            const src = escapeHtml(codeBlockContent.join('\n'));
            output.push(`<div style="background:var(--paper);border:1px solid var(--line);border-left:4px solid var(--zop-orange);padding:16px;margin:24px 0;color:var(--ink);font-size:12px;font-family:var(--mono);white-space:pre-wrap"><div style="font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--g-600);margin-bottom:8px">D2 source · preview only</div>${src}</div>`);
          }
        } else {
          output.push(`<pre><code class="language-${escapeHtml(codeBlockLang)}">${escapeHtml(codeBlockContent.join('\n'))}</code></pre>`);
        }
        codeBlockContent = [];
        codeBlockLang = '';
        inCodeBlock = false;
      } else {
        flushParagraph();
        flushList();
        flushBlockquote();
        flushTable();
        inCodeBlock = true;
        codeBlockLang = line.slice(3).trim();
      }
      continue;
    }
    if (inCodeBlock) {
      codeBlockContent.push(line);
      continue;
    }

    // Blank line
    if (line.trim() === '') {
      flushParagraph();
      flushList();
      flushBlockquote();
      continue;
    }

    // Headings
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      flushBlockquote();
      flushTable();
      const level = headingMatch[1].length;
      output.push(`<h${level}>${inlineMarkdown(headingMatch[2])}</h${level}>`);
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flushParagraph();
      flushList();
      flushBlockquote();
      flushTable();
      output.push('<hr>');
      continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      flushParagraph();
      flushList();
      flushTable();
      if (!inBlockquote) {
        output.push('<blockquote>');
        inBlockquote = true;
      }
      output.push(`<p>${inlineMarkdown(line.slice(2))}</p>`);
      continue;
    } else if (inBlockquote) {
      flushBlockquote();
    }

    // Pipe table — header row
    if (!inTable && line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const nextLine = i + 1 < lines.length ? lines[i + 1] : '';
      if (isTableSeparator(nextLine)) {
        flushParagraph();
        flushList();
        inTable = true;
        tableHeaders = parseTableRow(line);
        tableAligns = parseAlignments(nextLine);
        tableRows = [];
        i++; // skip separator
        continue;
      }
    }

    // Pipe table — data row
    if (inTable && line.trim().startsWith('|')) {
      tableRows.push(parseTableRow(line));
      continue;
    }

    // End of table
    if (inTable) {
      flushTable();
    }

    // Unordered list
    const ulMatch = line.match(/^[\s]*[-*+]\s+(.+)$/);
    if (ulMatch) {
      flushParagraph();
      if (inList !== 'ul') {
        flushList();
        output.push('<ul>');
        inList = 'ul';
      }
      output.push(`<li>${inlineMarkdown(ulMatch[1])}</li>`);
      continue;
    }

    // Ordered list
    const olMatch = line.match(/^[\s]*\d+[.)]\s+(.+)$/);
    if (olMatch) {
      flushParagraph();
      if (inList !== 'ol') {
        flushList();
        output.push('<ol>');
        inList = 'ol';
      }
      output.push(`<li>${inlineMarkdown(olMatch[1])}</li>`);
      continue;
    }

    // If we were in a list but this line doesn't match, flush
    if (inList) {
      flushList();
    }

    // Regular paragraph line
    paragraph.push(line);
  }

  // Flush remaining
  if (inCodeBlock) {
    if (codeBlockLang === 'd2') {
      output.push(`<div style="background:#1a2332;border-radius:8px;padding:1rem;margin:1.5rem 0;color:#7dd3fc;font-size:0.8em;font-family:monospace;border-left:3px solid #0891b2">[Diagram]</div>`);
    } else {
      output.push(`<pre><code>${escapeHtml(codeBlockContent.join('\n'))}</code></pre>`);
    }
  }
  flushTable();
  flushParagraph();
  flushList();
  flushBlockquote();

  // One-shot @keyframes for the skeleton loader; emitted only when at least one D2 fence used it.
  const keyframes = _d2Index > 0
    ? '<style>@keyframes d2-skeleton{0%{background-position:200% 0}100%{background-position:-200% 0}}</style>'
    : '';
  return keyframes + output.join('\n');
}

/** Process inline markdown: bold, italic, links, inline code, images */
function inlineMarkdown(text: string): string {
  let result = text;

  // Inline code (must be before other inline processing)
  result = result.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Images: ![alt](url)
  result = result.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" loading="lazy">');

  // Links: [text](url)
  result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

  // Bold+italic: ***text*** or ___text___
  result = result.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  result = result.replace(/___(.+?)___/g, '<strong><em>$1</em></strong>');

  // Bold: **text** or __text__
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  result = result.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Italic: *text* or _text_ (but not mid-word underscores)
  result = result.replace(/\*(.+?)\*/g, '<em>$1</em>');
  result = result.replace(/(?<!\w)_(.+?)_(?!\w)/g, '<em>$1</em>');

  // Strikethrough: ~~text~~
  result = result.replace(/~~(.+?)~~/g, '<del>$1</del>');

  return result;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
